import React from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Header3,
  LoadingAnimation,
  OptionSelectionButton,
  PopupContext,
  SimpleBlock,
  Text,
  TextLink,
  usePopupManager
} from 'ui-builder';

import { ExternalLink, Person, TeamPermission, useContactLinkService } from 'nekst-api';

import styles from './ExternalLinksList.module.scss';
import { FubConnectionContext, useFubConnectionContextRequired } from '../../../Integrations';
import { LinkExistingContactPopup } from './LinkExistingContactPopup';
import { ConfirmDeletePopup, ConfirmPositiveActionPopup, Separator } from 'features/nekst-widgets';
import { FubIntegrationToggleWrapper } from 'feature-toggle';
import { AccessChecker } from 'authorization-scope';
import FubConnectionBlock from './FubConnectionBlock';
import { LinkStatusWidget } from './LinkStatus';

function useUnlinkOption(person: Person, link: ExternalLink) {
  const contactLinkService = useContactLinkService();
  const popupManager = usePopupManager();

  const deleteFunc = async () => {
    await contactLinkService.unlinkContact(person.id, link.id);
    popupManager.closePopup();
  };

  const onClick = () => {
    popupManager.openPopup(
      <ConfirmDeletePopup
        deleteFunc={deleteFunc}
        message="Are you sure you want to unlink this contact?"
      />
    );
  };

  return {
    getOption: () => ({
      label: 'Unlink',
      onSelected: () => {
        onClick();
      },
      value: 'unlink'
    })
  };
}

function useExternalContactOption(person: Person, link: ExternalLink) {
  const contactLinkService = useContactLinkService();
  const popupManager = usePopupManager();

  const syncFunc = async () => {
    await contactLinkService.updateExternalContact(person.id, link.id);
    popupManager.closePopup();
  };

  return {
    getOption: () => ({
      label: 'Update FUB Contact',
      onSelected: () => {
        popupManager.openPopup(
          <ConfirmPositiveActionPopup
            title="Update Follow Up Boss Contact"
            message="Once confirmed, the Follow Up Boss contact will be updated with the latest information from Nekst. This may overwrite some existing data in Follow Up Boss."
            submitFormFunc={syncFunc}
          />
        );
      },
      value: 'unlink'
    })
  };
}

function useUpdateLocalContactOption(person: Person, link: ExternalLink) {
  const contactLinkService = useContactLinkService();
  const popupManager = usePopupManager();

  const syncFunc = async () => {
    await contactLinkService.updateNekstContact(person.id, link.id);
    popupManager.closePopup();
  };

  return {
    getOption: () => ({
      label: 'Update Nekst Contact',
      onSelected: () => {
        popupManager.openPopup(
          <ConfirmPositiveActionPopup
            title="Update Nekst Contact"
            message="Once confirmed, the Nekst contact will be updated with the latest information from Follow Up Boss. This may overwrite some existing data in Nekst."
            submitFormFunc={syncFunc}
          />
        );
      },
      value: 'unlink'
    })
  };
}

function useSyncOption(person: Person, link: ExternalLink) {
  const contactLinkService = useContactLinkService();
  const popupManager = usePopupManager();

  const syncFunc = async () => {
    await contactLinkService.triggerSync(person.id, link.id);
    popupManager.closePopup();
  };

  return {
    getOption: () => ({
      label: 'Sync',
      onSelected: () => {
        popupManager.openPopup(
          <ConfirmPositiveActionPopup
            title="Sync Contacts"
            message="Once confirmed, the contact will be synced"
            submitFormFunc={syncFunc}
          />
        );
      },
      value: 'sync'
    })
  };
}

function NotConflictingActionsButton(
  props: {
    person: Person,
    link: ExternalLink,
  }
) {

  const unlinkOption = useUnlinkOption(props.person, props.link);

  return (
    <OptionSelectionButton
      text="Actions"
      options={[
        useSyncOption(props.person, props.link)
          .getOption(),
        unlinkOption.getOption()
      ]}
      size={ButtonSize.SMALL}
    />
  );
}

function ConflictingActionsButton(
  props: {
    person: Person,
    link: ExternalLink,
  }
) {
  return (
    <OptionSelectionButton
      text="Actions"
      options={[
        useExternalContactOption(props.person, props.link)
          .getOption(),
        useUpdateLocalContactOption(props.person, props.link)
          .getOption(),
        useUnlinkOption(props.person, props.link)
          .getOption()
      ]}
      size={ButtonSize.SMALL}
    />
  );
}

function LinkActionsButton(
  props: {
    person: Person,
    link: ExternalLink,
  }
) {
  if (props.link.isConflicting) {
    return <ConflictingActionsButton {...props} />;
  } else {
    return <NotConflictingActionsButton {...props} />;
  }
}

function LinkStatus(props: {
  externalLink: ExternalLink
}) {
  return (
    <LinkStatusWidget
      isConflicting={props.externalLink.isConflicting}
      conflictingTitle={`Contact syncing is currently disabled due to a conflict. Nekst couldn't determine how to sync two unsynced records. To resolve this, please choose either "Update FUB Contact" or "Update Nekst Contact.". Once resolved, syncing will continue automatically.`}
    />
  );
}

function ExistingLinksList(
  props: {
    person: Person,
  }
) {
  const getConnectionLabel = (contactLink: ExternalLink) => {
    let result = contactLink._contactName;

    if (contactLink._contactEmail) {
      result += ' (' + contactLink._contactEmail + ')';
    }

    return result;
  };

  return (
    <>
      {props.person.externalLinks.map((link) => (
        <FubConnectionBlock
          content={(
            <SimpleBlock className={styles.nameWithStatus}>
              <TextLink href={link._externalProfileUrl} target="_blank">
                {getConnectionLabel(link)}
              </TextLink>
              <LinkStatus externalLink={link} />
            </SimpleBlock>
          )}
          actions={(
            <LinkActionsButton person={props.person} link={link} />
          )}
          isHorizontal
        />
      ))}
    </>
  );
}

function NoExistingLinksView(props: {
  person: Person
}) {

  const popupManager = usePopupManager();

  return (
    <FubConnectionBlock
      content={null}
      actions={(
        <Button
          text="Link Contact"
          variant={ButtonVariant.FILLED}
          size={ButtonSize.SMALL}
          onClick={() => {
            popupManager.openPopup(<LinkExistingContactPopup person={props.person} />);
          }}
        />
      )}
      isHorizontal
    />
  );
}

function NoFubConnectionView() {
  return (
    <Text>
      To integrate your contacts with other applications, see our current integration partners
      {' '}
      <TextLink href="/settings/integrations/fub">here</TextLink>.
    </Text>
  );
}

function ExternalLinksListView(
  props: {
    person: Person,
  }
) {
  const context = useFubConnectionContextRequired();

  let content;

  if (!context.loaded) {
    content = (<LoadingAnimation />);
  } else if (context.profiles.length === 0) {
    content = (<NoFubConnectionView />);
  } else if (props.person.externalLinks.length) {
    content = <ExistingLinksList person={props.person} />;
  } else {
    content = <NoExistingLinksView person={props.person} />;
  }

  return (
    <SimpleBlock>
      <Header3>Integrations</Header3>
      {content}
    </SimpleBlock>
  );
}

export function ExternalLinksList(props: {
  person: Person
}) {
  return (
    <FubConnectionContext>
      <PopupContext>
        <ExternalLinksListView person={props.person} />
      </PopupContext>
    </FubConnectionContext>
  );
}

export function ExternalLinksListBlock(props: {
  person: Person,
}) {
  return (
    <FubIntegrationToggleWrapper>
      <AccessChecker permissionToCheck={TeamPermission.READ_FUB_CONNECTION}>
        <Separator />
        <ExternalLinksList person={props.person} />
      </AccessChecker>
    </FubIntegrationToggleWrapper>
  );
}
